/**
 * Task WebSocket Service (Singleton)
 * Manages WebSocket connection for real-time task updates
 * Follows the same pattern as alertsAPI.js
 */

import { getToken } from '../utils/tokenStorage';

// Get store dynamically to avoid circular dependency
const getStore = () => {
  return require('../../store').store;
};

const getWsBaseUrl = () => {
  if (process.env.REACT_APP_WS_BASE_URL) {
    return process.env.REACT_APP_WS_BASE_URL;
  }
  if (window.location.hostname === 'fleetcamagile.com') {
    return 'wss://fleetcamagile.com';
  }
  return 'ws://localhost:8000';
};

class TaskService {
  constructor() {
    this.ws = null;
    this.reconnectInterval = null;
    this.reconnectAttempts = 0;
    this.heartbeatInterval = null;
    this.userId = null;
    this.isConnected = false;
  }

  initializeWebSocket(userId) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[TaskService] Already connected');
      return;
    }

    if (!userId) {
      console.log('[TaskService] No userId provided');
      return;
    }

    this.userId = userId;

    try {
      const WS_BASE_URL = getWsBaseUrl();
      const token = getToken();
      const wsUrl = `${WS_BASE_URL}/ws/tasks/?token=${token || ''}`;
      console.log('[TaskService] Connecting to:', wsUrl);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[TaskService] Connected successfully');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();

        const store = getStore();
        if (store && store.dispatch) {
          store.dispatch({ type: 'tasks/setConnected', payload: true });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('[TaskService] Error parsing message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[TaskService] WebSocket error:', error);
        this.isConnected = false;
      };

      this.ws.onclose = () => {
        console.log('[TaskService] Connection closed');
        this.isConnected = false;
        this.stopHeartbeat();

        const store = getStore();
        if (store && store.dispatch) {
          store.dispatch({ type: 'tasks/setConnected', payload: false });
        }

        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('[TaskService] Connection error:', error);
      this.isConnected = false;
    }
  }

  handleMessage(data) {
    const store = getStore();
    if (!store || !store.dispatch) return;

    switch (data.type) {
      case 'initial_data':
        console.log(`[TaskService] Initial data: ${data.tasks?.length} tasks`);
        store.dispatch({
          type: 'tasks/setTasks',
          payload: data.tasks || [],
        });
        break;

      case 'task_update':
        if (data.action === 'created') {
          console.log('[TaskService] New task:', data.task.title);
          store.dispatch({
            type: 'tasks/addTask',
            payload: data.task,
          });
        } else if (
          data.action === 'updated' ||
          data.action === 'status_changed' ||
          data.action === 'reassigned'
        ) {
          store.dispatch({
            type: 'tasks/updateTask',
            payload: data.task,
          });
        } else if (data.action === 'deleted') {
          store.dispatch({
            type: 'tasks/removeTask',
            payload: { id: data.task.id },
          });
        }
        break;

      case 'heartbeat':
      case 'pong':
        break;

      default:
        console.log('[TaskService] Unknown message type:', data.type);
    }
  }

  scheduleReconnect() {
    if (this.reconnectInterval || !this.userId) return;

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(
      `[TaskService] Reconnecting in ${delay / 1000}s (attempt ${this.reconnectAttempts})`
    );

    this.reconnectInterval = setTimeout(() => {
      this.reconnectInterval = null;
      this.initializeWebSocket(this.userId);
    }, delay);
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }

    this.stopHeartbeat();
    this.isConnected = false;
    this.userId = null;

    console.log('[TaskService] Disconnected');
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      readyState: this.ws?.readyState,
    };
  }
}

// Create singleton instance
const taskService = new TaskService();

export default taskService;
